import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CheckCircle2, XCircle, EyeOff, Eye, ExternalLink } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useReviewReportsT } from "@/lib/review-reports-i18n";
import {
  listReviewReports,
  resolveReviewReport,
  adminHideReview,
  type AdminReviewReport,
  type ReviewReportStatus,
} from "@/lib/reviews.functions";

export const Route = createFileRoute("/admin/recenze-reporty")({
  component: () => <ReviewReportsPage />,
});


type Tab = "open" | "resolved" | "dismissed" | "all";

function targetLink(review: AdminReviewReport["review"]): string | null {
  if (!review) return null;
  if (review.target_type === "business") return `/podnik/${review.target_id}`;
  return null; // menu_item / supplier require slugs — skip deep link
}

function ReviewReportsPage() {
  const t = useReviewReportsT();
  const { lang } = useT();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("open");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "review-reports", tab],
    queryFn: () => listReviewReports({ data: { status: tab } }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "review-reports"] });

  const resolveMut = useMutation({
    mutationFn: (v: { id: string; status: ReviewReportStatus; note?: string | null }) =>
      resolveReviewReport({ data: v }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const hideMut = useMutation({
    mutationFn: (v: { id: string; hidden: boolean }) =>
      adminHideReview({ data: { id: v.id, hidden: v.hidden } }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const fmt = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch {
      return iso.slice(0, 16).replace("T", " ");
    }
  };

  const statusBadge = (s: ReviewReportStatus) => {
    const variant: Record<ReviewReportStatus, "default" | "secondary" | "destructive"> = {
      open: "destructive",
      resolved: "default",
      dismissed: "secondary",
    };
    return <Badge variant={variant[s]}>{t(`status_${s}` as any)}</Badge>;
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <h1 className="font-serif text-2xl font-semibold text-brand-earth">{t("adminTitle")}</h1>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="open">{t("filterOpen")}</TabsTrigger>
          <TabsTrigger value="resolved">{t("filterResolved")}</TabsTrigger>
          <TabsTrigger value="dismissed">{t("filterDismissed")}</TabsTrigger>
          <TabsTrigger value="all">{t("filterAll")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("loading")}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">{t("adminEmpty")}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const link = targetLink(r.review);
            const isHidden = r.review?.hidden_by_admin === true;
            return (
              <li key={r.id}>
                <Card>
                  <CardContent className="space-y-3 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {statusBadge(r.status)}
                        <Badge variant="outline">{t(`reason_${r.reason}` as any)}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {t("createdCol")}: {fmt(r.created_at)}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {t("reporterCol")}: {r.reporter_email ?? r.reported_by.slice(0, 8)}
                      </span>
                    </div>

                    {r.detail && (
                      <div className="rounded-md bg-muted/40 p-2 text-sm whitespace-pre-line">
                        {r.detail}
                      </div>
                    )}

                    <div className="rounded-md border border-brand-earth/10 p-3">
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>
                          {t("reviewCol")} · {r.review?.rating ?? "?"}/10 ·{" "}
                          {r.review?.author_name_snapshot ?? "—"}
                        </span>
                        {isHidden ? (
                          <Badge variant="outline">{t("reviewHidden")}</Badge>
                        ) : (
                          <Badge variant="secondary">{t("reviewVisible")}</Badge>
                        )}
                      </div>
                      <p className="whitespace-pre-line text-sm">
                        {r.review?.comment || <span className="italic text-muted-foreground">—</span>}
                      </p>
                    </div>

                    {r.status === "open" && (
                      <Input
                        value={notes[r.id] ?? ""}
                        onChange={(e) => setNotes((s) => ({ ...s, [r.id]: e.target.value.slice(0, 500) }))}
                        placeholder={t("resolveNotePh")}
                      />
                    )}
                    {r.status !== "open" && r.resolution_note && (
                      <p className="text-xs text-muted-foreground italic">"{r.resolution_note}"</p>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {r.review && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => hideMut.mutate({ id: r.review!.id, hidden: !isHidden })}
                          disabled={hideMut.isPending}
                        >
                          {isHidden ? (
                            <><Eye className="mr-1 h-3.5 w-3.5" /> {t("actionUnhide")}</>
                          ) : (
                            <><EyeOff className="mr-1 h-3.5 w-3.5" /> {t("actionHide")}</>
                          )}
                        </Button>
                      )}
                      {r.status === "open" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => resolveMut.mutate({ id: r.id, status: "resolved", note: notes[r.id] ?? null })}
                            disabled={resolveMut.isPending}
                          >
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {t("actionResolve")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => resolveMut.mutate({ id: r.id, status: "dismissed", note: notes[r.id] ?? null })}
                            disabled={resolveMut.isPending}
                          >
                            <XCircle className="mr-1 h-3.5 w-3.5" /> {t("actionDismiss")}
                          </Button>
                        </>
                      )}
                      {link && (
                        <Button asChild size="sm" variant="ghost">
                          <Link to={link as any}>
                            <ExternalLink className="mr-1 h-3.5 w-3.5" /> {t("actionOpenReview")}
                          </Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
